import { useEffect, useMemo, useState } from "react";
import api, { money } from "@/lib/api";
import { Info, ArrowsLeftRight, X } from "@phosphor-icons/react";

/**
 * Composite editor for interest configuration on savings/loans.
 *
 * value: { method, rate }         (method is one of SI, CI_yearly, CI_quarterly, CI_monthly, CI_daily)
 * onChange({ method, rate })
 * previewKind?: "fd" | "rd" | "loan" — enables the live preview panel
 * previewParams?: { principal?, installment?, tenure_months? } — extra params for the preview call
 */

const FREQ_OPTIONS = [
  { value: "CI_yearly", label: "Year on year" },
  { value: "CI_quarterly", label: "Quarter on quarter" },
  { value: "CI_monthly", label: "Month on month" },
  { value: "CI_daily", label: "Day on day" },
];

export default function InterestPicker({
  value,
  onChange,
  previewKind,
  previewParams,
  allowDailyCompound = true,
  showLoanNote = false,
}) {
  const method = value?.method || "SI";
  const rate = value?.rate ?? "";
  const isCI = method !== "SI";
  const freq = isCI ? method : "CI_monthly";

  const setMethod = (m) => onChange({ ...value, method: m });
  const setRate = (r) => onChange({ ...value, rate: r });

  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");
  const [compareMethod, setCompareMethod] = useState(null); // e.g. "SI" or "CI_monthly"
  const [comparePreview, setComparePreview] = useState(null);

  const previewReady = useMemo(() => {
    if (!previewKind) return false;
    const p = previewParams || {};
    if (!(Number(rate) > 0)) return false;
    if (previewKind === "fd") return Number(p.principal) > 0 && Number(p.tenure_months) > 0;
    if (previewKind === "rd") return Number(p.installment) > 0 && Number(p.tenure_months) > 0;
    if (previewKind === "loan") return Number(p.principal) > 0 && Number(p.tenure_days) > 0;
    return false;
  }, [previewKind, previewParams, rate]);

  useEffect(() => {
    if (!previewReady) { setPreview(null); return; }
    const params = new URLSearchParams({
      kind: previewKind,
      method,
      rate: String(rate),
    });
    const p = previewParams || {};
    if (p.principal) params.set("principal", String(p.principal));
    if (p.installment) params.set("installment", String(p.installment));
    if (p.tenure_months) params.set("tenure_months", String(p.tenure_months));
    if (p.tenure_days) params.set("tenure_days", String(p.tenure_days));
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/finance/preview?${params.toString()}`);
        if (!cancelled) { setPreview(data); setPreviewErr(""); }
      } catch (e) {
        if (!cancelled) { setPreview(null); setPreviewErr(e.response?.data?.detail || "Preview error"); }
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [previewReady, previewKind, previewParams, method, rate]);

  // Comparison preview (only fetches when compareMethod is set)
  useEffect(() => {
    if (!previewReady || !compareMethod) { setComparePreview(null); return; }
    const params = new URLSearchParams({
      kind: previewKind,
      method: compareMethod,
      rate: String(rate),
    });
    const p = previewParams || {};
    if (p.principal) params.set("principal", String(p.principal));
    if (p.installment) params.set("installment", String(p.installment));
    if (p.tenure_months) params.set("tenure_months", String(p.tenure_months));
    if (p.tenure_days) params.set("tenure_days", String(p.tenure_days));
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/finance/preview?${params.toString()}`);
        if (!cancelled) setComparePreview(data);
      } catch {
        if (!cancelled) setComparePreview(null);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [previewReady, compareMethod, previewKind, previewParams, rate]);

  const freqOptions = allowDailyCompound
    ? FREQ_OPTIONS
    : FREQ_OPTIONS.filter((f) => f.value !== "CI_daily");

  return (
    <div className="space-y-3" data-testid="interest-picker">
      <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
        Interest configuration
      </div>

      <div className="grid grid-cols-2 gap-2">
        <MethodTile
          active={method === "SI"}
          title="Simple Interest"
          hint="Interest = P × r × t. Same every period."
          onClick={() => setMethod("SI")}
          testId="method-si"
        />
        <MethodTile
          active={isCI}
          title="Compound Interest"
          hint="Interest earns interest each period."
          onClick={() => setMethod(freq)}
          testId="method-ci"
        />
      </div>

      {isCI && (
        <div>
          <div className="text-xs font-medium text-slate-600 mb-1.5">Compounding frequency</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
            {freqOptions.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setMethod(f.value)}
                data-testid={`freq-${f.value}`}
                className={`text-xs px-2 py-1.5 rounded-md border font-medium transition-colors ${
                  method === f.value
                    ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <label className="block">
        <div className="text-xs font-medium text-slate-600 mb-1.5">Annual interest rate (%)</div>
        <input
          type="number"
          step="0.01"
          required
          value={rate}
          onChange={(e) => setRate(e.target.value === "" ? "" : Number(e.target.value))}
          data-testid="rate-input"
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
          placeholder="e.g. 7.5"
        />
      </label>

      {showLoanNote && isCI && method !== "CI_daily" && (
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2 flex gap-2">
          <Info size={14} className="shrink-0 mt-0.5 text-slate-500" />
          <span>
            On loans, compounding other than day-on-day uses a flat schedule — total interest is computed with the chosen compounding (prorated to days), then split evenly across the daily installments. Standard reducing-balance EMI is only used when <b>Day on day</b> is selected.
          </span>
        </div>
      )}

      {previewKind && (
        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/70" data-testid="interest-preview">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              Live preview
            </div>
            {previewReady && (
              compareMethod ? (
                <button
                  type="button"
                  onClick={() => setCompareMethod(null)}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-800"
                  data-testid="close-compare-btn"
                >
                  <X size={12} weight="bold" /> Close comparison
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setCompareMethod(method === "SI" ? "CI_monthly" : "SI")}
                  className="inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:underline font-medium"
                  data-testid="compare-btn"
                >
                  <ArrowsLeftRight size={12} weight="bold" /> Compare with another method
                </button>
              )
            )}
          </div>

          {!previewReady ? (
            <div className="text-xs text-slate-400">Fill in rate, tenure and amount to see the calculation.</div>
          ) : previewErr ? (
            <div className="text-xs text-rose-600">{String(previewErr)}</div>
          ) : preview ? (
            !compareMethod ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                {"emi" in preview && <PreviewCell label="Daily EMI" value={money(preview.emi)} accent />}
                {"maturity" in preview && <PreviewCell label="Maturity value" value={money(preview.maturity)} accent />}
                {"total_deposited" in preview && <PreviewCell label="Total deposited" value={money(preview.total_deposited)} />}
                {"total_payable" in preview && <PreviewCell label="Total payable" value={money(preview.total_payable)} />}
                {"total_interest" in preview && <PreviewCell label="Total interest" value={money(preview.total_interest)} />}
              </div>
            ) : (
              <ComparePanel
                currentLabel={methodLabel(method)}
                current={preview}
                otherLabel={methodLabel(compareMethod)}
                other={comparePreview}
                onSwap={() => onChange({ ...value, method: compareMethod })}
                pickerFreqOptions={freqOptions}
                compareMethod={compareMethod}
                setCompareMethod={setCompareMethod}
              />
            )
          ) : (
            <div className="text-xs text-slate-400">Calculating…</div>
          )}
        </div>
      )}
    </div>
  );
}

function MethodTile({ active, title, hint, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left p-3 rounded-lg border transition-colors ${
        active
          ? "border-emerald-500 bg-emerald-50"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>
    </button>
  );
}

function PreviewCell({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
      <div className={`mono heading text-base font-semibold ${accent ? "text-emerald-700" : "text-slate-900"}`}>
        {value}
      </div>
    </div>
  );
}

const METHOD_LABELS = {
  SI: "Simple Interest",
  CI_yearly: "CI · Year on year",
  CI_quarterly: "CI · Quarter on quarter",
  CI_monthly: "CI · Month on month",
  CI_daily: "CI · Day on day",
};

function methodLabel(m) {
  return METHOD_LABELS[m] || m;
}

function ComparePanel({ currentLabel, current, otherLabel, other, onSwap, compareMethod, setCompareMethod }) {
  const allMethods = ["SI", "CI_yearly", "CI_quarterly", "CI_monthly", "CI_daily"];
  return (
    <div className="space-y-3">
      <div>
        <div className="text-[11px] text-slate-500 mb-1">Compare against</div>
        <div className="flex gap-1 flex-wrap">
          {allMethods.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setCompareMethod(m)}
              data-testid={`compare-with-${m}`}
              className={`text-[11px] px-2 py-1 rounded-md border font-medium ${
                compareMethod === m
                  ? "border-sky-500 bg-sky-50 text-sky-800"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
              }`}
            >
              {methodLabel(m)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <ComparisonColumn
          title={currentLabel}
          data={current}
          tone="emerald"
          selected
        />
        <ComparisonColumn
          title={otherLabel}
          data={other}
          tone="sky"
          actionLabel="Use this"
          onAction={onSwap}
        />
      </div>
    </div>
  );
}

function ComparisonColumn({ title, data, tone, selected, actionLabel, onAction }) {
  const toneClasses = tone === "emerald"
    ? "border-emerald-200 bg-emerald-50/40"
    : "border-sky-200 bg-sky-50/40";
  return (
    <div className={`border ${toneClasses} rounded-lg p-3`} data-testid={`compare-col-${tone}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-slate-800">{title}</div>
        {selected && <span className="text-[9px] uppercase tracking-widest bg-emerald-600 text-white rounded-full px-1.5 py-0.5">Selected</span>}
      </div>
      {!data ? (
        <div className="text-xs text-slate-400">Calculating…</div>
      ) : (
        <div className="space-y-1.5 text-xs">
          {"emi" in data && <CompareRow k="EMI" v={money(data.emi)} strong />}
          {"maturity" in data && <CompareRow k="Maturity" v={money(data.maturity)} strong />}
          {"total_interest" in data && <CompareRow k="Total interest" v={money(data.total_interest)} />}
          {"total_payable" in data && <CompareRow k="Total payable" v={money(data.total_payable)} />}
          {"total_deposited" in data && <CompareRow k="Total deposited" v={money(data.total_deposited)} />}
        </div>
      )}
      {!selected && data && onAction && (
        <button
          type="button"
          onClick={onAction}
          data-testid="use-comparison-btn"
          className="mt-3 w-full text-xs font-medium bg-sky-600 hover:bg-sky-700 text-white rounded-md py-1.5"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

function CompareRow({ k, v, strong }) {
  return (
    <div className="flex justify-between gap-2">
      <div className="text-slate-500">{k}</div>
      <div className={`mono ${strong ? "font-semibold text-slate-900" : "text-slate-700"}`}>{v}</div>
    </div>
  );
}
